import { Injectable } from '@angular/core';
import { serializePermissionSource } from '../../common-files/permission-tree';

// what a Super Admin is currently viewing the app as (View As Role)
export interface ViewAsState {
  roleId: number;
  roleKey: string;
  roleName: string;
  applicableFor: string; // CENTRAL | ESTABLISHMENT | BOTH
  establishmentId: number | null;
  establishmentName: string | null;
}

// Super Admin "View As Role": swaps the stored permission tree / context for the
// chosen role's template so the menu, the route guard and the in-screen gating
// behave like that role, and the interceptor sends x-view-as-role so the API
// checks the same template. The Super Admin's own context is snapshotted and put
// back on exit — no re-login needed.
@Injectable({ providedIn: 'root' })
export class ViewAsService {
  private static readonly STATE_KEY = 'viewAs';
  private static readonly BACKUP_KEY = 'viewAsBackup';
  // the context keys that change while viewing as a role
  private static readonly CONTEXT_KEYS = [
    'permissions',
    'activeScope',
    'activeEstablishmentId',
    'activeEstablishmentName',
    'roleName',
  ];

  current(): ViewAsState | null {
    try {
      const raw = localStorage.getItem(ViewAsService.STATE_KEY);
      return raw ? (JSON.parse(raw) as ViewAsState) : null;
    } catch {
      return null;
    }
  }

  isActive(): boolean {
    return this.current() !== null;
  }

  // start viewing as `state` with the role's permissionsV2 tree
  enter(state: ViewAsState, permissionsV2: any[]): void {
    if (!this.isActive()) {
      const backup: { [key: string]: string | null } = {};
      for (const key of ViewAsService.CONTEXT_KEYS) backup[key] = localStorage.getItem(key);
      localStorage.setItem(ViewAsService.BACKUP_KEY, JSON.stringify(backup));
    }
    localStorage.setItem('permissions', serializePermissionSource({ permissionsV2 }));
    if (state.establishmentId) {
      localStorage.setItem('activeScope', 'ESTABLISHMENT');
      localStorage.setItem('activeEstablishmentId', String(state.establishmentId));
      localStorage.setItem('activeEstablishmentName', state.establishmentName || '');
    } else {
      localStorage.setItem('activeScope', 'GLOBAL');
      localStorage.removeItem('activeEstablishmentId');
      localStorage.removeItem('activeEstablishmentName');
    }
    localStorage.removeItem('activePropertyId');
    localStorage.removeItem('activePropertyName');
    localStorage.setItem('roleName', `Viewing as ${state.roleName}`);
    localStorage.setItem(ViewAsService.STATE_KEY, JSON.stringify(state));
  }

  // back to the Super Admin's own context
  exit(): void {
    try {
      const raw = localStorage.getItem(ViewAsService.BACKUP_KEY);
      const backup = raw ? (JSON.parse(raw) as { [key: string]: string | null }) : {};
      for (const key of ViewAsService.CONTEXT_KEYS) {
        const value = backup[key];
        if (value === null || value === undefined) localStorage.removeItem(key);
        else localStorage.setItem(key, value);
      }
    } catch {
      // nothing to restore
    }
    localStorage.removeItem(ViewAsService.STATE_KEY);
    localStorage.removeItem(ViewAsService.BACKUP_KEY);
  }

  // forget everything (logout / fresh login)
  clear(): void {
    localStorage.removeItem(ViewAsService.STATE_KEY);
    localStorage.removeItem(ViewAsService.BACKUP_KEY);
  }
}
